import { FormattedMessage, useIntl } from 'react-intl';
import { Form, FormInstance, Popover, Skeleton, Table } from 'antd';
import { useCompaniesReduced } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { ColumnsType, InputDecimal } from '@/components/_common';
import { useEffect } from 'react';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useHistory } from 'react-router-dom';
import { Info } from 'react-feather';
import { getTableScrollX } from '@/utils/table';
import { CompanyReduced } from '@/core/domain/enterprise/Companies/Companies.types';
import FormItemBorder from '@/components/_common/FormItemBorder';

interface TaskPricesFormProps {
  form: FormInstance;
  edit?: boolean;
}

function TaskPricesForm({ form, edit }: TaskPricesFormProps) {
  const intl = useIntl();

  const history = useHistory();
  const params = useQueryParams<{ companyId?: string }>();

  const { data, isFetching, isLoading } = useCompaniesReduced({
    active: true,
    rows: 50,
    isCompanyFilter: true,
  });

  const companies = flatInfiniteData(data);

  useEffect(() => {
    if (params.companyId && !isLoading) {
      const rowElement = document.querySelector<HTMLDivElement>(
        `[data-row-key="company-${params.companyId}"]`,
      );

      if (rowElement) {
        window.scrollTo({ top: rowElement.offsetTop - 100 });

        rowElement.querySelectorAll('td').forEach((td) => {
          td.style.background = 'rgba(255, 165, 0, 0.25)';
        });

        setTimeout(() => {
          rowElement.querySelectorAll('td').forEach((td) => {
            td.style.background = '';
          });
          history.replace({ search: '' });
        }, 5000);
      }
    }
  }, [params, isLoading]);

  useEffect(() => {
    if (companies.length && !edit) {
      form.setFieldsValue({
        taskPriceSet: companies.map((company) => ({
          companyId: company.id,
          companyName: company.name,
        })),
      });
    }
  }, [companies, edit]);

  const columns: ColumnsType<CompanyReduced> = [
    {
      title: intl.formatMessage({ id: 'tasks.form.taskPrices.company' }),
      dataIndex: 'name',
      key: 'companyName',
      fixed: 'left',
      width: 220,
    },

    {
      title: intl.formatMessage({ id: 'tasks.form.taskPrices.price' }),
      dataIndex: 'price',
      key: 'price',
      width: 160,
      render: (_, __, index: number) => (
        <>
          <Form.Item hidden name={[index, 'id']} />
          <Form.Item hidden name={[index, 'companyId']} />

          <FormItemBorder name={[index, 'price']}>
            <InputDecimal
              disabled={
                !hasPermissions([Authorities.REGISTER_TASK_CHANGE_PRICES])
              }
              placeholder="Preço"
            />
          </FormItemBorder>
        </>
      ),
    },

    {
      title: intl.formatMessage({
        id: 'tasks.form.taskPrices.fixedServiceCost',
      }),
      dataIndex: 'priceFixed',
      key: 'priceFixed',
      width: 160,
      render: (_, __, index: number) => (
        <FormItemBorder name={[index, 'priceFixed']}>
          <InputDecimal
            disabled={
              !hasPermissions([Authorities.REGISTER_TASK_CHANGE_PRICES])
            }
            placeholder="Custo fixo"
          />
        </FormItemBorder>
      ),
    },

    {
      title: intl.formatMessage({
        id: 'tasks.form.taskPrices.commissionPercentage',
      }),
      dataIndex: 'commissionPercentage',
      key: 'commissionPercentage',
      width: 200,
      render: (_, __, index: number) => (
        <FormItemBorder name={[index, 'commissionPercentage']}>
          <InputDecimal
            disabled={
              !hasPermissions([Authorities.REGISTER_TASK_CHANGE_PRICES])
            }
            placeholder="Comissão (%)"
            suffix={
              <Popover
                content={
                  <div style={{ maxWidth: 300 }}>
                    Este valor será utilizado no cálculo e exibido no
                    comissionamento da OS, mesmo que o funcionário indicado
                    possua uma comissão configurada em seu cadastro.
                  </div>
                }
              >
                <Info size={18} />
              </Popover>
            }
          />
        </FormItemBorder>
      ),
    },
  ];

  return (
    <div id="task-form-prices">
      <div className="create-page-title" style={{ marginTop: '24px' }}>
        <h2>
          <FormattedMessage id="tasks.form.taskPrices" />
        </h2>
      </div>

      <Skeleton active loading={isFetching}>
        <Form.List name="taskPriceSet">
          {() => (
            <Table
              dataSource={companies}
              columns={columns}
              pagination={false}
              loading={isLoading}
              scroll={{ x: getTableScrollX(columns) }}
              rowKey={(row) => `company-${row.id}`}
              className="table-small"
            />
          )}
        </Form.List>
      </Skeleton>
    </div>
  );
}

export default TaskPricesForm;
